import { Observable, of } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { FirestoreDoc } from '../../../../../commons/firestore/firestore-doc.model'
import {
	addDoc,
	collection,
	collectionChanges,
	collectionData,
	collectionGroup,
	collectionSnapshots,
	CollectionReference,
	deleteDoc,
	doc,
	docData,
	DocumentChange,
	DocumentReference,
	Firestore,
	query,
	QueryConstraint,
	QueryDocumentSnapshot,
	setDoc,
} from '@angular/fire/firestore'
import { inject } from '@angular/core'

export type FirestorePathParams = { [s: string]: string }

export abstract class FirestoreService<T extends FirestoreDoc, PP extends FirestorePathParams = never> {
	protected readonly firestore: Firestore = inject(Firestore)

	protected constructor() {}

	protected get collectionGroupPath(): string | undefined {
		return undefined
	}

	generateUid(): string {
		return doc(collection(this.firestore, 'dummy')).id
	}

	read(constraints?: Array<QueryConstraint>, pathParams?: PP): Observable<Array<T>> {
		const collectionRef: CollectionReference<T> = collection(
			this.firestore,
			this.resolvePath(pathParams),
		) as CollectionReference<T>
		const queryRef = query(collectionRef, ...(constraints ?? []))
		return collectionData<T, 'uid'>(queryRef, { idField: 'uid' })
	}

	readSnapshots(constraints?: Array<QueryConstraint>, pathParams?: PP): Observable<Array<QueryDocumentSnapshot<T>>> {
		const collectionRef: CollectionReference<T> = collection(
			this.firestore,
			this.resolvePath(pathParams),
		) as CollectionReference<T>
		const queryRef = query(collectionRef, ...(constraints ?? []))
		return collectionSnapshots(queryRef)
	}

	readGroupSnapshots(constraints?: Array<QueryConstraint>): Observable<Array<QueryDocumentSnapshot<T>>> {
		if (!this.collectionGroupPath) {
			throw new Error('Collection group path is not defined')
		}
		const collectionGroupRef: CollectionReference<T> = collectionGroup(
			this.firestore,
			this.collectionGroupPath,
		) as CollectionReference<T>
		const queryRef = query(collectionGroupRef, ...(constraints ?? []))
		return collectionSnapshots(queryRef)
	}

	readCollectionChanges(constraints?: Array<QueryConstraint>, pathParams?: PP): Observable<Array<DocumentChange<T>>> {
		const collectionRef: CollectionReference<T> = collection(
			this.firestore,
			this.resolvePath(pathParams),
		) as CollectionReference<T>
		const queryRef = query(collectionRef, ...(constraints ?? []))
		return collectionChanges<T>(queryRef)
	}

	readGroup(constraints?: Array<QueryConstraint>): Observable<Array<T>> {
		if (!this.collectionGroupPath) {
			throw new Error('Collection group path is not defined')
		}
		const collectionGroupRef: CollectionReference<T> = collectionGroup(
			this.firestore,
			this.collectionGroupPath,
		) as CollectionReference<T>
		const queryRef = query(collectionGroupRef, ...(constraints ?? []))
		return collectionData<T, 'uid'>(queryRef, { idField: 'uid' })
	}

	readGroupChanges(constraints?: Array<QueryConstraint>): Observable<Array<DocumentChange<T>>> {
		if (!this.collectionGroupPath) {
			throw new Error('Collection group path is not defined')
		}
		const collectionGroupRef: CollectionReference<T> = collectionGroup(
			this.firestore,
			this.collectionGroupPath,
		) as CollectionReference<T>
		const queryRef = query(collectionGroupRef, ...(constraints ?? []))
		return collectionChanges<T>(queryRef)
	}

	get(uid: string, pathParams?: PP): DocumentReference<T> {
		return doc(this.firestore, `${this.resolvePath(pathParams)}/${uid}`) as DocumentReference<T>
	}

	getValue(uid: string, pathParams?: PP): Observable<T | null> {
		return this.observeValues(uid, pathParams).pipe(take(1))
	}

	observeValues(uid: string, pathParams?: PP): Observable<T | null> {
		if (!uid) {
			return of(null)
		}

		return docData(this.get(uid, pathParams)).pipe(map((doc) => doc ?? null))
	}

	async add(payload: T, pathParams?: PP): Promise<void | null> {
		if (!payload) {
			return null
		}

		const parsedPayload = this.preSave(payload)

		if (!payload.uid) {
			parsedPayload.uid = this.generateUid()
		}

		const docRef = this.get(payload.uid, pathParams)
		await setDoc(docRef, { ...parsedPayload })
	}

	// async set(payload: T, options?: SetOptions, pathParams?: PP): Promise<void | null> {
	// 	if (!payload) {
	// 		return null
	// 	}
	//
	// 	this.assertHasUid(payload)
	// 	const parsedPayload = this.preSave(payload)
	// 	const docRef = this.get(parsedPayload.uid as string, pathParams)
	//
	// 	return setDoc(docRef, { ...parsedPayload }, options ?? {})
	// }

	async update(payload: T, pathParams?: PP): Promise<void | null> {
		if (!payload) {
			return null
		}

		this.assertHasUid(payload)

		const parsedPayload = this.preUpdate(payload)
		const docRef = this.get(parsedPayload.uid as string, pathParams)

		return setDoc(docRef, { ...parsedPayload }, { merge: true })
	}

	delete(uid: string, pathParams?: PP): Promise<void> {
		return deleteDoc(this.get(uid, pathParams))
	}

	protected abstract resolvePath(params?: PP): string

	protected preSave(doc: T): T {
		// @ts-expect-error need this to preserve functionality, this will be populated by the onWrite fn
		delete doc.updatedAt
		// @ts-expect-error need this to preserve functionality, this will be populated by the onWrite fn
		delete doc.createdAt
		return doc
	}

	protected preUpdate(doc: T): T {
		return this.preSave(doc)
	}

	private assertHasUid(doc: T): void {
		if (!doc.uid) {
			throw new Error(`Missing or empty value for 'uid' property of document: ${JSON.stringify(doc)}`)
		}
	}
}
